/* eslint-disable max-lines-per-function */
import {Box, Divider, Flex, P} from 'exsportia-components'
import {TextType} from 'exsportia-components/dist/types'
import {useFormikContext} from 'formik'
import {DateTime} from 'luxon'
import React, {FC, useCallback, useEffect, useMemo, useRef} from 'react'
import {useQuery} from 'react-query'
import {useParams} from 'react-router-dom'

import {BookingQueries} from '../../api/booking'
import {
  CalculateBookingPriceQueryParams,
  CalculatedBookingPriceObject,
  LimitationCheckResultType,
  LimitationCheckResultTypeKey,
} from '../../api/booking/calculate-booking-price'
import {BookingUsageDataType, Participant} from '../../api/booking/types'
import {mergeApplicable} from '../../api/promo-codes/card-state'
import {sumPromoDiscount} from '../../api/promo-codes/helpers'
import {AddonQuoteProblem} from '../../components/addon-quote-problem/addon-quote-problem'
import {ClassCard} from '../../components/class-card/class-card'
import {InstructorCard} from '../../components/instructor-card/instructor-card'
import {PlayspaceCard} from '../../components/playspace-card/playspace-card'
import {PricingDetails} from '../../components/pricing-details/pricing-details'
import {PromoCodeField} from '../../components/promo-code-field/promo-code-field'
import {useFormikPromoCode} from '../../components/promo-code-field/use-formik-promo-code'
import {SubscriptionCard} from '../../components/subscription-card/subscription-card'
import {ParticipantsList} from '../../components/timeslot-grid/components/participants-list'
import {usePublicCancelPolicy} from '../../api/venue/get-public-cancel-policy'
import {VenueCancelPolicyInfo} from '../../components/venue-cancel-policy-info/venue-cancel-policy-info'
import {FULL_CANCEL_POLICY_OPTIONS_MAP} from '../../constants/cancel-policy'
import {USAGE_TYPE_SUBSCRIPTION_OPTIONS_LABEL_MAP} from '../../constants/usage-type'
import {getParticipantsEquipment} from '../../helpers/data'
import {safeRound} from '../../helpers/logic'
import {AreaService} from '../../services/area'
import {CreateClassBookingFormType} from '../../services/booking/forms/create-class-booking-form'
import {ClassService} from '../../services/class'
import {ClassSessionsService} from '../../services/class-sessions'
import {useServiceOptions} from '../../services/service-options/hooks'
import {VenueService} from '../../services/venue'
import {theme} from '../../theme/theme'
import {CheckBox} from '../../ui/components/input/checkbox'
import {TextArea} from '../../ui/components/input/text-area'
import {LabeledValueRow} from '../../ui/components/key-value-row/labeled-value-row'
import {LabeledSection} from '../../ui/components/labeled-section/labeled-section'
import {TextWithShowMore} from '../../ui/components/text-with-show-more/text-with-show-more'
import {TitledSection} from '../../ui/components/titeled-section/titled-section'
import {auth} from '../../utils/firebase'
import {LocaleOptions, useLocale, useLuxon} from '../../utils/i18n'
import {isNotNilAndNotEmpty} from '../../utils/logic'
import {useModalActions} from '../../utils/modals'
import {NO_USER_PHOTO} from '../../utils/remote-content'

const defaultLOptions: LocaleOptions = {caseAction: 'capitalize'}

export const ClassSummarySection: FC<{
  setIsLoading: (arg: boolean) => void
  setWholePrice?: (arg?: number) => void
  // eslint-disable-next-line max-lines-per-function
}> = ({setIsLoading, setWholePrice}) => {
  const {l, language} = useLocale()
  const luxon = useLuxon()
  const {openModal} = useModalActions()
  const params = useParams<{venueId: string}>()
  const {data: venueData, isLoading: isVenueLoading} = useQuery(
    VenueService.queries.getVenueQuery({params: {id: params.venueId}}),
  )
  const {values, setFieldValue} = useFormikContext<CreateClassBookingFormType>()
  const {data: classData, isLoading: isClassLoading} = useQuery(
    ClassService.queries.getClassQuery({params: {id: values.classId}}),
  )
  const {getServiceConfigByServiceType} = useServiceOptions([classData?.sportType])
  const {data: areaData} = useQuery(AreaService.queries.getAreaQuery({params: {id: values.areaId}}))
  const {data: classSessionData} = useQuery(
    ClassSessionsService.queries.getClassSessionQuery({params: {id: values.sessionId}}),
  )
  // The server refuses a quote without venueId (#4035). The quote key never changes, so a request sent
  // before the venue query answered was never sent again. The URL's venueId is the venue doc id.
  const venueId = venueData?.uid || params.venueId
  const isMultiSession = values.selectedSessions?.length > 1
  const stripMembershipFields = (el: any) => {
    const copy = {...el}
    delete copy.membership
    delete copy.membershipActivity
    return copy
  }
  const queryData: CalculateBookingPriceQueryParams = useMemo(() => {
    if (isMultiSession) {
      return {
        options: values.selectedSessions.map((session) => {
          const sessionParticipants = values.perSessionParticipants?.[session.sessionId] || values.participants
          const sessionUseForParticipants =
            values.perSessionUseForParticipants?.[session.sessionId] !== undefined
              ? values.perSessionUseForParticipants[session.sessionId]
              : values.useForParticipants
          return {
            bookingType: 'CLASS' as const,
            venueId,
            classId: session.classId,
            slotId: session.sessionId,
            sportType: session.sportType || values.sportType,
            coverEquipment: values.coverEquipment,
            userId: values.userId || auth.currentUser?.uid || undefined,
            useForParticipants: sessionUseForParticipants,
            date: session.date,
            participants: sessionParticipants.map(stripMembershipFields),
          }
        }),
        promoCode: values.promoCode || undefined,
      }
    }
    return {
      options: [
        {
          bookingType: 'CLASS' as const,
          venueId,
          classId: values.classId,
          slotId: values.sessionId,
          sportType: values.sportType,
          coverEquipment: values.coverEquipment,
          userId: values.userId || auth.currentUser?.uid || undefined,
          useForParticipants: values.useForParticipants,
          date: luxon.fromMillis(Number(classSessionData?.date_timestamp)).toFormat('dd/MM/yyyy'),
          participants: values.participants.map(stripMembershipFields),
        },
      ],
      promoCode: values.promoCode || undefined,
    }
  }, [
    venueId,
    classSessionData?.date_timestamp,
    isMultiSession,
    values.selectedSessions,
    values.promoCode,
    // A pass taken off after the server refused it must reach the next quote.
    values.participants,
    values.perSessionParticipants,
    values.perSessionUseForParticipants,
    values.useForParticipants,
  ])
  const hasParticipants = isMultiSession
    ? Object.values(values.perSessionParticipants || {}).some((p) => p.length > 0) || values.participants.length > 0
    : values.participants.length > 0
  const hasTimestamp = isMultiSession ? values.selectedSessions.length > 0 : !!classSessionData?.date_timestamp
  const {data, error, isLoading, isFetching, refetch, dataUpdatedAt} = useQuery(
    BookingQueries.calculateBookingPriceQuery({
      enabled: !!venueId && hasParticipants && hasTimestamp,
      query: queryData,
      refetchOnWindowFocus: false,
    }),
  )
  const isFirstPromoRender = useRef(true)
  // Which passes the participants carry. When it changes (the server refused one and it was taken off)
  // the price is asked for again; nothing else about the participants triggers a new quote here.
  const passSignature = useMemo(
    () =>
      [values.participants, ...Object.values(values.perSessionParticipants || {})]
        .map((list) => list.map((p) => p.membershipBookingId || '').join(','))
        .join('|'),
    [values.participants, values.perSessionParticipants],
  )
  useEffect(() => {
    if (isFirstPromoRender.current) {
      isFirstPromoRender.current = false
      return
    }
    if (venueId && hasParticipants && hasTimestamp) {
      void refetch({cancelRefetch: true})
    }
  }, [values.promoCode, passSignature])
  const promoApplied = useMemo(() => data?.find((el) => el.promoApplied)?.promoApplied ?? null, [data])
  const promoError = useMemo(
    () => (promoApplied ? null : data?.find((el) => el.promoError)?.promoError ?? null),
    [data, promoApplied],
  )
  const promoDiscount = useMemo(() => safeRound(sumPromoDiscount(data)), [data])
  const promoApplicable = useMemo(() => (data ? mergeApplicable(data.map((el) => el.applicableCopies)) : undefined), [data])
  const promoField = useFormikPromoCode({promoApplied, promoError, isQuoting: isFetching, quotedAt: dataUpdatedAt})
  const promoItems = useMemo(() => {
    const classIds = isMultiSession
      ? values.selectedSessions.map((session) => session.classId)
      : [values.classId]
    return Array.from(new Set(classIds.filter(Boolean) as string[])).map((entityId) => ({
      entityType: 'class' as const,
      entityId,
    }))
  }, [isMultiSession, values.selectedSessions, values.classId])

  const wholeDiscount = useMemo(() => (data ? data.reduce((acc, el) => acc + Number(el.discount), 0) : 0), [data])
  const initialDiscount = useMemo(
    () => (data ? data.reduce((acc, el) => acc + Number(el.totalInitialDiscount), 0) : 0),
    [data],
  )
  const totalPrice = useMemo(() => (data ? data.reduce((acc, el) => acc + Number(el.totalPrice), 0) : 0), [data])
  // All sessions belong to the same class? Used to decide the summary header title. WBA-4047.
  const allSameClass = useMemo(
    () => isMultiSession && values.selectedSessions.every((s) => s.classId === values.selectedSessions[0].classId),
    [isMultiSession, values.selectedSessions],
  )
  // The price query is sent in selectedSessions order, so data[i] maps to selectedSessions[i].
  // Build a sessionId -> price lookup so each session card can show its own price/discount. WBA-4055.
  const priceBySessionId = useMemo(() => {
    const map: Record<string, CalculatedBookingPriceObject> = {}
    if (data && isMultiSession) {
      values.selectedSessions.forEach((s, i) => {
        if (data[i]) {
          map[s.sessionId] = data[i]
        }
      })
    }
    return map
  }, [data, isMultiSession, values.selectedSessions])
  // isFetching, not just isLoading: while a new quote is on its way (after a pass was taken off) the
  // old one must not be payable, because the booking is built from the quote.
  useEffect(() => {
    setIsLoading(isLoading || isFetching)
  }, [isLoading, isFetching, setIsLoading])
  useEffect(() => {
    // Use the total payable across all sessions (not just the first) so the payment
    // step is triggered whenever any session has a charge — e.g. session 1 is fully
    // covered by a subscription (price 0) but later sessions are paid. WBA-4101.
    setWholePrice?.(data ? data.reduce((acc, el) => acc + Number(el.price), 0) : undefined)
  }, [isLoading, data, setWholePrice])
  const {bookingDateTime, bookingDuration} = useMemo<{
    bookingDateTime: DateTime
    bookingDuration: string
  }>(() => {
    if (!classSessionData) {
      // VALIDATE FORM AND NAVIGATE
      return {bookingDateTime: luxon.now(), bookingDuration: '-'}
    }
    return {
      bookingDateTime: luxon.fromMillis(Number(classSessionData.date_timestamp)),
      bookingDuration: l('minutes_short', {value: classSessionData.duration}),
    }
  }, [l, luxon, classSessionData])
  const {label} = useMemo(() => getServiceConfigByServiceType(classData?.sportType), [classData])

  const cancelPolicyToUse = values.participants[0]?.membership?.cancelPolicy ?? classData?.cancelPolicy
  const cancelPolicyKey = cancelPolicyToUse && FULL_CANCEL_POLICY_OPTIONS_MAP[cancelPolicyToUse]?.description
  const cancelPolicyDescription = cancelPolicyKey ? l(cancelPolicyKey) : cancelPolicyToUse
  // No ladder step: the venue's own policy decides (the pass's, else the class's), as on the server.
  const venuePolicyId = cancelPolicyToUse
    ? null
    : values.participants[0]?.membership?.venueCancelPolicyId || classData?.venueCancelPolicyId || null
  const {data: venuePolicy} = usePublicCancelPolicy(venueData?.uid, venuePolicyId)

  const bookingPriceData = useMemo(() => {
    if (!data) {
      return []
    }
    return [
      ...data.map((el: CalculatedBookingPriceObject, index: number) => {
        const timeArr = []
        if (el.duration.hours) {
          timeArr.push(l('hours_short', {value: el.duration.hours}))
        }
        if (el.duration.minutes) {
          timeArr.push(l('minutes_short', {value: el.duration.minutes}))
        }
        const priceAndDiscount = el.participants.reduce(
          (acc, participant) => ({
            price: safeRound(Number(acc.price) + Number(participant.activityPrice)),
            discount: safeRound(Number(acc.discount) + Number(participant.totalDiscount)),
            initialDiscount: safeRound(Number(acc.initialDiscount) + Number(participant.totalInitialDiscount)),
          }),
          {
            price: 0,
            discount: 0,
            initialDiscount: 0,
          },
        )
        // Each session row must show its OWN class name, not the (shared) sport label. WBA-4049.
        const sessionClassName = isMultiSession ? values.selectedSessions[index]?.className : ''
        return {
          discount: priceAndDiscount.discount,
          count: el.numberOfPeople,
          price: priceAndDiscount.price,
          initialDiscount: priceAndDiscount.initialDiscount,
          text: l('service_price_element', {time: timeArr.join(' '), service: sessionClassName || l(label)}),
        }
      }),
      ...getParticipantsEquipment(data.reduce<Participant[]>((acc, el) => [...acc, ...el.participants], [])),
    ]
  }, [data, l]) // eslint-disable-line

  const usageData = useMemo(
    () =>
      data
        ? data[0]?.participants.reduce(
            (acc, participant) => {
              const used: LimitationCheckResultType | null = participant.limitationCheckResult?.used || null
              if (!used) {
                return acc
              }
              const firstPropName: LimitationCheckResultTypeKey | null = Object.keys(
                used,
              )[0] as LimitationCheckResultTypeKey
              return {
                used: acc.used + (used[firstPropName] || 0),
                amountByType: USAGE_TYPE_SUBSCRIPTION_OPTIONS_LABEL_MAP[firstPropName],
              }
            },
            {
              used: 0,
              amountByType: USAGE_TYPE_SUBSCRIPTION_OPTIONS_LABEL_MAP.entries,
            },
          )
        : null,
    [data],
  ) as BookingUsageDataType | null

  // Collect every distinct subscription applied across sessions, summing its usage per session,
  // so multi-session bookings show ALL subscriptions (not just the first). data[i] is aligned
  // with selectedSessions[i] (same order the price query was sent in). WBA-4056.
  const multiSessionSubscriptions = useMemo(() => {
    if (!isMultiSession || !data) {
      return []
    }
    const map: Record<string, {name: string; imageUrl: string; usage: BookingUsageDataType}> = {}
    values.selectedSessions.forEach((session, i) => {
      const sessionParticipants = values.perSessionParticipants?.[session.sessionId] || values.participants
      const membership = sessionParticipants.find((p) => p.membership)?.membership
      const sessionData = data[i]
      if (!membership || !sessionData) {
        return
      }
      const key = membership.membershipBookingId || membership.uid || membership.name
      const sessionUsage = sessionData.participants.reduce(
        (acc, participant) => {
          const used: LimitationCheckResultType | null = participant.limitationCheckResult?.used || null
          if (!used) {
            return acc
          }
          const firstPropName = Object.keys(used)[0] as LimitationCheckResultTypeKey
          return {
            used: acc.used + (used[firstPropName] || 0),
            amountByType: USAGE_TYPE_SUBSCRIPTION_OPTIONS_LABEL_MAP[firstPropName],
          }
        },
        {used: 0, amountByType: USAGE_TYPE_SUBSCRIPTION_OPTIONS_LABEL_MAP.entries},
      ) as BookingUsageDataType
      if (!map[key]) {
        map[key] = {
          name: membership.name,
          imageUrl: membership.logo?.secure_url || '',
          usage: {used: 0, amountByType: sessionUsage.amountByType},
        }
      }
      map[key].usage = {
        used: map[key].usage.used + sessionUsage.used,
        amountByType: sessionUsage.used > 0 ? sessionUsage.amountByType : map[key].usage.amountByType,
      }
    })
    return Object.values(map)
  }, [isMultiSession, data, values.selectedSessions, values.perSessionParticipants, values.participants])

  const playspaceCard = useMemo(() => {
    if (!areaData) {
      return null
    }
    let subTextElements: unknown[] = []
    if (isNotNilAndNotEmpty(areaData.areaSize)) {
      subTextElements = [areaData.areaSize]
    }
    if (isNotNilAndNotEmpty(areaData.surfaceType)) {
      subTextElements = [...subTextElements, l(areaData.surfaceType, {caseAction: 'capitalize'})]
    }
    return (
      <PlayspaceCard
        styleType='square'
        image={
          <Box
            backgroundSize='cover'
            height='48px'
            width='72px'
            background={`url(${areaData.media?.[areaData.thumbIndex || 0]?.secure_url}) no-repeat center`}
          />
        }
        name={areaData.name}
        subTextElements={subTextElements as TextType[]}
      />
    )
  }, [values, language])

  const openParticipantsModalFor = useCallback(
    (participants: typeof values.participants) => {
      openModal({
        title: l('participants'),
        footerConfig: {
          disabled: true,
        },
        component: (
          <ParticipantsList
            participants={participants}
            displayEquipmentCount={true}
          />
        ),
      })
    },
    [openModal, l],
  )

  useEffect(() => {
    setFieldValue('session', classSessionData || null)
  }, [classSessionData])

  useEffect(() => {
    setFieldValue('class', classData || null)
  }, [classData])

  return (
    <Box>
      <AddonQuoteProblem error={error} />
      <ClassCard
        isLoading={isVenueLoading || isClassLoading}
        venueName={venueData?.name}
        className={
          isMultiSession
            ? allSameClass
              ? values.selectedSessions[0]?.className || classData?.name
              : l('classes')
            : classData?.name
        }
        subTextElements={[
          isMultiSession
            ? `${values.selectedSessions.length} ${l('sessions') || 'sessions'} · ${values.participants.length} ${l(
                'participants',
              )}`
            : `${values.participants.length} ${l('participants')}`,
        ]}
        image={
          <Box
            backgroundSize='cover'
            height='72px'
            width='72px'
            background={`url(${
              classData?.media?.[classData.thumbIndex || 0]?.secure_url || NO_USER_PHOTO
            }) no-repeat center`}
          />
        }
      />
      <TitledSection title={l('booking_information', defaultLOptions)}>
        {isMultiSession ? (
          <Box>
            {(() => {
              const groups: Record<string, typeof values.selectedSessions> = {}
              for (const s of values.selectedSessions) {
                if (!groups[s.date]) {
                  groups[s.date] = []
                }
                groups[s.date].push(s)
              }
              return Object.entries(groups)
                .sort(([a], [b]) => {
                  const dA = luxon.fromFormat(a, 'dd/MM/yyyy')
                  const dB = luxon.fromFormat(b, 'dd/MM/yyyy')
                  return dA.toMillis() - dB.toMillis()
                })
                .map(([dateKey, sessions]) => {
                  const displayDate = luxon.fromFormat(dateKey, 'dd/MM/yyyy').toFormat('ccc, MMM d, yyyy')
                  return (
                    <Box key={dateKey}>
                      <Flex
                        justifyContent='center'
                        py='8px'
                      >
                        <P
                          fontSize='12px'
                          color='#718096'
                        >
                          {displayDate}
                        </P>
                      </Flex>
                      {sessions.map((session) => {
                        const instructor = session.instructors?.[0]
                        const sessionParticipants =
                          values.perSessionParticipants?.[session.sessionId] || values.participants
                        const sessionPrice = priceBySessionId[session.sessionId]
                        return (
                          <Box
                            key={session.sessionId}
                            p='12px 16px'
                            border='1px solid #e2e8f0'
                            borderRadius='12px'
                            mb='8px'
                          >
                            <Flex
                              alignItems='baseline'
                              justifyContent='space-between'
                            >
                              <Flex alignItems='baseline'>
                                <P
                                  fontSize='16px'
                                  fontWeight='bold'
                                >
                                  {session.time}
                                </P>
                                <P
                                  fontSize='12px'
                                  color='#718096'
                                  ml='6px'
                                >
                                  {session.duration}min
                                </P>
                              </Flex>
                              {sessionPrice && (
                                <P
                                  fontSize='14px'
                                  fontWeight='bold'
                                  color='#1a202c'
                                >
                                  {venueData?.currency}
                                  {safeRound(Number(sessionPrice.totalPrice))}
                                </P>
                              )}
                            </Flex>
                            {session.className && (
                              <P
                                fontSize='13px'
                                fontWeight='600'
                                color='#1a202c'
                                mt='2px'
                              >
                                {session.className}
                              </P>
                            )}
                            <Flex
                              alignItems='center'
                              justifyContent='space-between'
                              mt='4px'
                            >
                              <Flex alignItems='center'>
                                {instructor && (
                                  <Box
                                    as='img'
                                    src={instructor.profileImage?.secure_url || ''}
                                    width='24px'
                                    height='24px'
                                    borderRadius='50%'
                                    mr='8px'
                                  />
                                )}
                                <P
                                  fontSize='14px'
                                  color='#4a5568'
                                >
                                  {instructor?.name || ''}
                                </P>
                              </Flex>
                              <Flex
                                cursor='pointer'
                                onClick={() => openParticipantsModalFor(sessionParticipants)}
                              >
                                <P
                                  fontSize='12px'
                                  color='primary'
                                >
                                  {sessionParticipants.length} {l('participants')}
                                </P>
                              </Flex>
                            </Flex>
                            {sessionPrice && Number(sessionPrice.discount) > 0 && (
                              <Flex
                                justifyContent='flex-end'
                                mt='2px'
                              >
                                <P
                                  fontSize='12px'
                                  color='#38a169'
                                >
                                  -{venueData?.currency}
                                  {safeRound(Number(sessionPrice.discount))} {l('discount')}
                                </P>
                              </Flex>
                            )}
                          </Box>
                        )
                      })}
                    </Box>
                  )
                })
            })()}
          </Box>
        ) : (
          <>
            <LabeledValueRow label={l('dateAndTime')}>
              {bookingDateTime.toUTC().toFormat('HH:mm, MMM dd, yyyy')}
            </LabeledValueRow>
            <LabeledValueRow label={l('duration')}>{bookingDuration}</LabeledValueRow>
            <LabeledValueRow label={l('participants')}>
              <Flex
                cursor='pointer'
                onClick={() => openParticipantsModalFor(values.participants)}
              >
                {`${values.participants.length} ${l('participants')}`}
              </Flex>
            </LabeledValueRow>
          </>
        )}
      </TitledSection>
      <TitledSection title={l('about', defaultLOptions)}>
        {playspaceCard && <LabeledSection label={l('playSpace', defaultLOptions)}>{playspaceCard}</LabeledSection>}
        {values.instructors && isNotNilAndNotEmpty(values.instructors) && (
          <LabeledSection label={l('instructors')}>
            {values.instructors.map((instructor, index) => (
              <Box
                mt={'8px'}
                key={`instructor-card-${index}`}
              >
                <InstructorCard
                  key={instructor.uid || instructor.name}
                  name={instructor.name}
                  image={
                    <Box
                      width='48px'
                      height='48px'
                      backgroundSize='cover'
                      background={`url(${instructor.profileImage?.secure_url || NO_USER_PHOTO}) no-repeat center`}
                    />
                  }
                />
              </Box>
            ))}
          </LabeledSection>
        )}
      </TitledSection>
      {isMultiSession
        ? multiSessionSubscriptions.length > 0 && (
            <TitledSection title={l('subscription')}>
              {multiSessionSubscriptions.map((sub, idx) => (
                <Box
                  key={`${sub.name}-${idx}`}
                  mt={idx === 0 ? '0' : '8px'}
                >
                  <SubscriptionCard
                    styleType='summary'
                    isLoading={isLoading}
                    usage={sub.usage}
                    imageUrl={sub.imageUrl}
                    name={sub.name}
                  />
                </Box>
              ))}
            </TitledSection>
          )
        : values.participants[0]?.membership && (
            <TitledSection title={l('subscription')}>
              <SubscriptionCard
                styleType='summary'
                isLoading={isLoading}
                usage={usageData}
                imageUrl={values.participants[0].membership.logo?.secure_url || ''}
                name={values.participants[0].membership.name}
              />
            </TitledSection>
          )}
      <TitledSection title={l('extra_details', defaultLOptions)}>
        <LabeledSection label={l('description')}>
          <TextWithShowMore text={values.class?.description ?? '-'} />
        </LabeledSection>
        <LabeledSection label={l('additionalInformation')}>
          <TextWithShowMore text={values.class?.arrivalInstructions ?? '-'} />
        </LabeledSection>
        {venuePolicy ? (
          <LabeledSection label={l('cancelPolicy')}>
            <VenueCancelPolicyInfo
              policy={venuePolicy}
              startMs={classSessionData ? Number(classSessionData.date_timestamp) : null}
            />
          </LabeledSection>
        ) : (
          cancelPolicyDescription && (
            <LabeledSection label={l('cancelPolicy')}>
              <TextWithShowMore text={cancelPolicyDescription} />
            </LabeledSection>
          )
        )}
        <Box>
          <Flex
            py={theme.space.multiplier0_5x}
            flexDirection={'row'}
            justifyContent={'space-between'}
            alignItems={'center'}
          >
            <P fontSize={'tag'}>{l('specialRequest')}</P>
            <Box>
              <CheckBox id={'hasSpecialRequests'} />
            </Box>
          </Flex>
          {values.hasSpecialRequests && (
            <Box pt={'8px'}>
              <TextArea
                id={'specialRequest'}
                detached
              />
            </Box>
          )}
        </Box>
      </TitledSection>
      <PromoCodeField
        {...promoField}
        venueId={venueData?.uid}
        items={promoItems}
        applied={promoApplied}
        applicable={promoApplicable}
        isQuoting={isFetching}
        currency={venueData?.currency}
        hidden={isLoading || totalPrice === 0}
      />
      <Divider />
      <Box mt='8px'>
        <PricingDetails
          total={totalPrice}
          isLoading={isLoading}
          discount={wholeDiscount}
          values={bookingPriceData}
          initialDiscount={initialDiscount}
          withoutCurrency={false}
          currency={venueData?.currency ?? ''}
          promo={promoApplied && promoDiscount > 0 ? {code: promoApplied.code, amount: promoDiscount} : null}
        />
      </Box>
    </Box>
  )
}

export const ClassPricingAndPaymentsSection: FC<{
  setIsLoading: (arg: boolean) => void
  setWholePrice?: (arg?: number) => void
  // eslint-disable-next-line max-lines-per-function
}> = ({setIsLoading, setWholePrice}) => {
  const {l} = useLocale()
  const luxon = useLuxon()
  const {values, setFieldValue} = useFormikContext<CreateClassBookingFormType>()
  const params = useParams<{venueId: string}>()
  const {data: venueData} = useQuery(VenueService.queries.getVenueQuery({params: {id: params.venueId}}))
  const venueId = venueData?.uid || params.venueId
  const {data: classSessionData} = useQuery(
    ClassSessionsService.queries.getClassSessionQuery({params: {id: values.sessionId}}),
  )
  const {data: classData} = useQuery(ClassService.queries.getClassQuery({params: {id: values.classId}}))
  const {getServiceConfigByServiceType} = useServiceOptions([classData?.sportType])
  const {label} = useMemo(() => getServiceConfigByServiceType(classData?.sportType), [classData])

  const queryData: CalculateBookingPriceQueryParams = useMemo(
    () => ({
      options: [
        {
          bookingType: 'CLASS',
          venueId,
          classId: values.classId,
          slotId: values.sessionId,
          sportType: values.sportType,
          coverEquipment: values.coverEquipment,
          userId: values.userId || auth.currentUser?.uid || undefined,
          useForParticipants: values.useForParticipants,
          date: luxon.fromMillis(Number(classSessionData?.date_timestamp)).toFormat('dd/MM/yyyy'),
          participants: values.participants.map((el) => {
            const elToStave = {...el}
            delete elToStave.membership
            delete elToStave.membershipActivity
            return elToStave
          }),
        },
      ],
      promoCode: values.promoCode || undefined,
    }),
    [venueId, classSessionData?.date_timestamp, values.promoCode],
  )
  const {data, error, isLoading} = useQuery(
    BookingQueries.calculateBookingPriceQuery({
      enabled: !!venueId && !!values.participants.length && !!classSessionData?.date_timestamp,
      query: queryData,
      refetchOnWindowFocus: false,
    }),
  )
  useEffect(() => {
    setIsLoading(isLoading)
  }, [isLoading, setIsLoading])
  useEffect(() => {
    setWholePrice?.(data?.[0].price)
  }, [isLoading, data, setWholePrice])

  useEffect(() => {
    if (data) {
      setFieldValue('price', data[0].price)
    }
  }, [data, setFieldValue])

  const bookingPriceData = useMemo(() => {
    if (!data) {
      return []
    }
    return [
      ...data.map((el: CalculatedBookingPriceObject) => {
        const timeArr = []
        if (el.duration.hours) {
          timeArr.push(l('hours_short', {value: el.duration.hours}))
        }
        if (el.duration.minutes) {
          timeArr.push(l('minutes_short', {value: el.duration.minutes}))
        }
        const priceAndDiscount = el.participants.reduce(
          (acc, participant) => ({
            price: Number(acc.price) + Number(participant.activityTotalPrice),
            discount: Number(acc.discount) + Number(participant.totalDiscount),
            initialDiscount: Number(acc.initialDiscount) + Number(participant.totalInitialDiscount),
          }),
          {
            price: 0,
            discount: 0,
            initialDiscount: 0,
          },
        )
        return {
          discount: priceAndDiscount.discount,
          count: el.numberOfPeople,
          price: priceAndDiscount.price,
          initialDiscount: priceAndDiscount.initialDiscount,
          text: l('service_price_element', {time: timeArr.join(' '), service: l(label)}),
        }
      }),
      ...getParticipantsEquipment(data.reduce<Participant[]>((acc, el) => [...acc, ...el.participants], [])),
    ]
  }, [data, l]) // eslint-disable-line

  const wholeDiscount = useMemo(() => (data ? data.reduce((acc, el) => acc + Number(el.totalDiscount), 0) : 0), [data])

  const initialDiscount = useMemo(
    () => (data ? data.reduce((acc, el) => acc + Number(el.totalInitialDiscount), 0) : 0),
    [data],
  )
  const totalPrice = useMemo(() => (data ? data.reduce((acc, el) => acc + Number(el.totalPrice), 0) : 0), [data])

  return (
    <Box>
      <AddonQuoteProblem error={error} />
      <PricingDetails
        total={totalPrice}
        isLoading={isLoading}
        values={bookingPriceData}
        discount={wholeDiscount}
        initialDiscount={initialDiscount}
        withoutCurrency={false}
        currency={venueData?.currency}
      />
    </Box>
  )
}
